import React, {useMemo, useRef, useState} from 'react'
import UserImageCircle from '../../../components/UserImageCircle'
import Form, {FormInputComponent} from '../../../components/form/Form'
import {Affix, InputNumber, Radio} from "antd";
import ProjectPosition from '../../../models/ProjectPosition';
import {usePosition} from "../../../providers/ProjectPositionProvider";
import User from "../../../models/User";
import {useSearchParams} from "react-router-dom";
import ChangeOrderItemImpactPanel from "./ChangeOrderItemImpactPanel";
import SearchTalentInput from "../../../components/form/SearchTalentInput";
import SpinLoader from "../../../components/SpinLoader";
import {useAuth} from "../../../providers/AuthProvider";
import {useMount} from "react-use";


const TimelineInputs = ({formModel, readOnly, ...props}) => {
    const {auth} = useAuth();
    const canManageClients = auth.canManageClients();

    const {position, isDisabled} = usePosition();

    const lastCandidateSegment = useMemo(() => {
        let segment = TB.last(position.candidate_segments);
        segment.platform_fee = segment.platform_cost - segment.talent_cost;
        segment.candidate.user = User.make(segment.candidate.user)
        return segment;
    }, [position]);


    return (
        <div className="flex flex-col gap-4 pb-2 mb-4">
            <p className="font-semibold">Contract Terms</p>

            <div className="flex flex-row items-start gap-3">
                <div className="flex-1">
                    <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                        Talent Resource
                    </p>
                    <div className="pt-1.5 flex items-stretch flex-col">

                        <div className="pt-1.5 flex flex-row items-center gap-2">
                            <UserImageCircle user={lastCandidateSegment.candidate.user}/>
                            <p>
                                {lastCandidateSegment.candidate.user.fullName()}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="flex-1">
                    {canManageClients && (
                        <React.Fragment>
                            <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                            Tax Classification
                        </p>
                            <div className="pt-1.5 flex items-stretch flex-col">

                                {lastCandidateSegment.candidate && (
                                    <p className="pt-1.5">
                                        {TaxTypes[lastCandidateSegment.candidate.tax_type]}
                                    </p>
                                )}
                            </div>
                        </React.Fragment>
                    )}
                </div>
                <div className="flex-1">
                    <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                        Start Date
                    </p>
                    <div className="pt-1.5 flex items-stretch flex-col">

                        {lastCandidateSegment.started_at && (
                            <p className="pt-1.5">
                                {TB.moment(lastCandidateSegment.started_at).format('MMM Do, YYYY')}
                            </p>
                        )}
                    </div>
                </div>
            </div>
            <div className="flex flex-row items-start gap-3">
                <div className="flex-1">
                    <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                        Original End Date
                    </p>
                    <div className="pt-1.5 flex items-stretch flex-col">

                        {lastCandidateSegment.ended_at && (
                            <p className="pt-1.5">
                                {TB.moment(lastCandidateSegment.ended_at).format('MMM Do, YYYY')}
                            </p>
                        )}
                    </div>
                </div>

                <div className="flex-1">
                    <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                        New End Date
                    </p>
                    <div className="pt-1.5 flex items-stretch flex-col max-w-[200px]">

                        <FormInputComponent
                            fieldName="ended_at"
                            value={TB.moment(formModel.ended_at)}
                            label={false}
                            type="date"
                            disabled={isDisabled}
                            readOnly={readOnly}
                            bordered={!readOnly}
                            validationErrors={{
                                isDefaultRequiredValue: TB.trans('validation.required', {
                                    'attribute': 'end date',
                                })
                            }}
                            required
                        />

                    </div>
                </div>
                <div className="flex-1"/>
            </div>
            <div className="flex flex-row items-start gap-3">

                <div className="flex-1">
                    <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                        Total Hours
                    </p>

                    <div className="pt-1.5 flex items-stretch flex-col">

                        <p className="pt-1.5">
                            {lastCandidateSegment.total_periods || lastCandidateSegment.total_hours || '-'}
                        </p>

                    </div>
                </div>
                <div className="flex-1">
                    <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                        New Total Hours
                    </p>
                    <div className="pt-1.5 flex items-stretch flex-col max-w-[200px]">

                        <FormInputComponent
                            fieldName="total_periods"
                            value={formModel.total_periods}
                            type="antd-input"
                            disabled={isDisabled}
                            placeholder="--"
                            min={0}
                            // max={formModel.original_total_periods}
                            label={false}
                            required
                        >
                            <InputNumber
                                controls={false}
                                readOnly={readOnly}
                                bordered={!readOnly}
                                style={readOnly ? {padding: 0} : undefined}
                                defaultValue={lastCandidateSegment.total_periods || lastCandidateSegment.total_hours}
                                step={1}
                                formatter={value => Number(value)}
                            />
                        </FormInputComponent>
                    </div>
                </div>
                <div className="flex-1"/>
            </div>
            <div className="flex flex-row items-start gap-3">

                <div className="flex-1">
                    {canManageClients &&  (
                        <React.Fragment>
                            <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                                Talent Hourly Rate
                            </p>
                            <div className="pt-1.5 flex flex-row items-center gap-2">
                                <div className="pt-1.5 flex flex-row items-center gap-2">
                                    ${TB.formatNumber((lastCandidateSegment.rate || lastCandidateSegment.hourly_rate), 2)} / hr
                                </div>
                            </div>
                        </React.Fragment>
                    )}
                </div>
                <div className="flex-1">
                    {canManageClients &&  (
                        <React.Fragment>
                            <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                                Percent Markup
                            </p>

                            <div className="pt-1.5 flex flex-row items-center gap-2">
                                <div className="pt-1.5 flex flex-row items-center gap-2">
                                    {parseFloat(Number(lastCandidateSegment.percent_markup).toFixed(4))}
                                </div>
                            </div>
                        </React.Fragment>
                    )}
                </div>
                <div className="flex-1">
                    <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                        Assigned Bill Rate
                    </p>

                    <div className="pt-1.5 flex flex-row items-center gap-2">
                        <div className="pt-1.5 flex flex-row items-center gap-2">
                            ${TB.formatNumber(lastCandidateSegment.platform_rate, 2)} / hr
                        </div>
                    </div>
                </div>
            </div>
            <div className="flex flex-row items-start gap-3">
                <div className="flex-1">
                    {canManageClients &&  (
                        <React.Fragment>
                            <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                                Resource Cost
                            </p>

                            <div className="pt-1.5 flex flex-row items-center gap-2">
                                <div className="pt-1.5 flex flex-row items-center gap-2">
                                    <span className="flex flex-row items-center gap-3">
                                        {Number(lastCandidateSegment.talent_cost).toFixed(2) !== Number(formModel.talent_cost)
                                            .toFixed(2) && (
                                            <span
                                                className="text-danger line-through">${TB.formatNumber(lastCandidateSegment.talent_cost, 2)}</span>
                                        )}
                                        <span>${TB.formatNumber(formModel.talent_cost, 2)}</span>
                                    </span>
                                </div>
                            </div>
                        </React.Fragment>
                    )}
                </div>
                <div className="flex-1">
                    {canManageClients &&  (
                        <React.Fragment>
                            <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                                Platform Fee
                            </p>

                            <div className="pt-1.5 flex flex-row items-center gap-2">
                                <div className="pt-1.5 flex flex-row items-center gap-2">
                                    <span className="flex flex-row items-center gap-3">
                                        {Number(lastCandidateSegment.platform_fee).toFixed(2) !== Number(formModel.platform_fee)
                                            .toFixed(2) && (
                                            <span
                                                className="text-danger line-through">${TB.formatNumber(lastCandidateSegment.platform_fee, 2)}</span>
                                        )}
                                        <span>${TB.formatNumber(formModel.platform_fee, 2)}</span>
                                    </span>
                                </div>
                            </div>
                        </React.Fragment>
                    )}
                </div>
                <div className="flex-1">
                    <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                        Contract Value
                    </p>

                    <div className="pt-1.5 flex flex-row items-center gap-2">
                        <div className="pt-1.5 flex flex-row items-center gap-2">
                            <span className="flex flex-row items-center gap-3">
                                <span>${TB.formatNumber(formModel.platform_cost, 2)}</span>
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

const RateInputs = ({formModel, readOnly, ...props}) => {
    const {auth} = useAuth();
    const canManageClients = auth.canManageClients();

    const {position, isDisabled} = usePosition();

    const lastCandidateSegment = useMemo(() => {
        let segment = TB.last(position.candidate_segments);
        segment.platform_fee = segment.platform_cost - segment.talent_cost;
        segment.candidate.user = User.make(segment.candidate.user)
        return segment;
    }, [position]);


    return (
        <React.Fragment>
            <div className="flex flex-col gap-4 pb-2 mb-4">
                <p className="font-semibold">Current Contract Terms</p>

                <div className="flex flex-row items-start gap-3">
                    <div className="flex-1">
                        <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                            Talent Resource
                        </p>
                        <div className="pt-1.5 flex items-stretch flex-col">

                            <div className="pt-1.5 flex flex-row items-center gap-2">
                                <UserImageCircle user={lastCandidateSegment.candidate.user}/>
                                <p>
                                    {lastCandidateSegment.candidate.user.fullName()}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="flex-1">
                        {canManageClients &&  (
                            <React.Fragment>
                                <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                                    Tax Classification
                                </p>
                                <div className="pt-1.5 flex items-stretch flex-col">

                                    {lastCandidateSegment.candidate && (
                                        <p className="pt-1.5">
                                            {TaxTypes[lastCandidateSegment.candidate.tax_type]}
                                        </p>
                                    )}
                                </div>
                            </React.Fragment>
                        )}
                    </div>
                    <div className="flex-1">
                        <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                            Start Date
                        </p>
                        <div className="pt-1.5 flex items-stretch flex-col">

                            {lastCandidateSegment.started_at && (
                                <p className="pt-1.5">
                                    {TB.moment(lastCandidateSegment.started_at).format('MMM Do, YYYY')}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex flex-row items-start gap-3">
                    <div className="flex-1">
                        {canManageClients &&  (
                            <React.Fragment>
                                <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                                    Current Hourly Rate
                                </p>
                                <div className="pt-1.5 flex flex-row items-center gap-2">
                                    <div className="pt-1.5 flex flex-row items-center gap-2">
                                        ${TB.formatNumber((lastCandidateSegment.rate || lastCandidateSegment.hourly_rate), 2)} / hr
                                    </div>
                                </div>
                            </React.Fragment>
                        )}
                    </div>
                    <div className="flex-1">
                        <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                            Current Bill Rate
                        </p>
                        <div className="pt-1.5 flex flex-row items-center gap-2">
                            <div className="pt-1.5 flex flex-row items-center gap-2">
                                ${TB.formatNumber(lastCandidateSegment.platform_rate, 2)} / hr
                            </div>
                        </div>
                    </div>
                    <div className="flex-1">
                        {canManageClients &&  (
                            <React.Fragment>
                                <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                                    Current Percent Markup
                                </p>

                                <div className="pt-1.5 flex flex-row items-center gap-2">
                                    <div className="pt-1.5 flex flex-row items-center gap-2">
                                        <span className="flex flex-row items-center gap-3">
                                            <span>{parseFloat(Number(lastCandidateSegment.percent_markup * 100).toFixed(4))}%</span>
                                        </span>
                                    </div>
                                </div>
                            </React.Fragment>
                        )}
                    </div>
                </div>


                <div className="flex flex-row items-start gap-3">
                    <div className="flex-1">
                        <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                            Original End Date
                        </p>
                        <div className="pt-1.5 flex items-stretch flex-col">

                            {lastCandidateSegment.ended_at && (
                                <p className="pt-1.5">
                                    {TB.moment(lastCandidateSegment.ended_at).format('MMM Do, YYYY')}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="flex-1">
                        <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                            End Date for Old Rate
                        </p>
                        <div className="pt-1.5 flex items-stretch flex-col max-w-[200px]">

                            <FormInputComponent
                                fieldName="effective_at"
                                value={TB.moment(formModel.effective_at)}
                                label={false}
                                type="date"
                                readOnly={readOnly}
                                bordered={!readOnly}
                                disabled={isDisabled}
                                validationErrors={{
                                    isDefaultRequiredValue: TB.trans('validation.required', {
                                        'attribute': 'end date',
                                    })
                                }}
                                required
                            />

                        </div>
                    </div>
                    <div className="flex-1"/>
                </div>


                <div className="flex flex-row items-start gap-3">

                    <div className="flex-1">
                        <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                            Current Est. Hours
                        </p>

                        <div className="pt-1.5 flex items-stretch flex-col">

                            <p className="pt-1.5">
                                {lastCandidateSegment.total_hours || lastCandidateSegment.total_periods || '-'}
                            </p>

                        </div>
                    </div>
                    <div className="flex-1">
                        <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                            Est. Hours for Old Rate
                        </p>
                        <div className="pt-1.5 flex items-stretch flex-col max-w-[200px]">

                            <FormInputComponent
                                fieldName="last_segment_periods"
                                value={formModel.last_segment_periods}
                                type="antd-input"
                                disabled={isDisabled}
                                placeholder="--"
                                min={0}
                                // max={formModel.original_total_periods}
                                label={false}
                                required
                            >
                                <InputNumber
                                    controls={false}
                                    readOnly={readOnly}
                                    bordered={!readOnly}
                                    style={readOnly ? {padding: 0} : undefined}
                                    defaultValue={lastCandidateSegment.total_hours || lastCandidateSegment.total_periods}
                                    step={1}
                                    formatter={value => Number(value)}
                                />
                            </FormInputComponent>
                        </div>
                    </div>
                    <div className="flex-1"/>
                </div>
                <div className="flex flex-row items-start gap-3">
                    <div className="flex-1">
                        {canManageClients &&  (
                            <React.Fragment>
                                <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                                    Old Rate Resource Cost
                                </p>

                                <div className="pt-1.5 flex flex-row items-center gap-2">
                                    <div className="pt-1.5 flex flex-row items-center gap-2">
                                        <span className="flex flex-row items-center gap-3">
                                            {Number(lastCandidateSegment.talent_cost).toFixed(2) !== Number((lastCandidateSegment.rate || lastCandidateSegment.hourly_rate) * formModel.last_segment_periods).toFixed(2) && (
                                                <span className="text-danger line-through">${TB.formatNumber(lastCandidateSegment.talent_cost, 2)}</span>
                                            )}
                                            <span>${TB.formatNumber((lastCandidateSegment.rate || lastCandidateSegment.hourly_rate) * formModel.last_segment_periods, 2)}</span>
                                        </span>
                                    </div>
                                </div>
                            </React.Fragment>
                        )}
                    </div>
                    <div className="flex-1">
                        {canManageClients &&  (
                            <React.Fragment>
                                <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                                    Old Rate Platform Fee
                                </p>

                                <div className="pt-1.5 flex flex-row items-center gap-2">
                                    <div className="pt-1.5 flex flex-row items-center gap-2">
                                        <span className="flex flex-row items-center gap-3">
                                            {Number(lastCandidateSegment.platform_fee).toFixed(2) !== Number((lastCandidateSegment.platform_rate * formModel.last_segment_periods) - ((lastCandidateSegment.rate || lastCandidateSegment.hourly_rate) * formModel.last_segment_periods)).toFixed(2) && (
                                                <span className="text-danger line-through">${TB.formatNumber(lastCandidateSegment.platform_fee, 2)}</span>
                                            )}
                                            <span>${TB.formatNumber((lastCandidateSegment.platform_rate * formModel.last_segment_periods) - ((lastCandidateSegment.rate || lastCandidateSegment.hourly_rate) * formModel.last_segment_periods), 2)}</span>
                                        </span>
                                    </div>
                                </div>
                            </React.Fragment>
                        )}
                    </div>
                    <div className="flex-1">
                        <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                            Old Rate Contract Value
                        </p>

                        <div className="pt-1.5 flex flex-row items-center gap-2">
                            <div className="pt-1.5 flex flex-row items-center gap-2">
                                <span className="flex flex-row items-center gap-3">
                                    {Number(lastCandidateSegment.platform_cost).toFixed(2) !== Number(lastCandidateSegment.platform_rate * formModel.last_segment_periods).toFixed(2) && (
                                        <span className="text-danger line-through">${TB.formatNumber(lastCandidateSegment.platform_cost, 2)}</span>
                                    )}
                                    <span>${TB.formatNumber(lastCandidateSegment.platform_rate * formModel.last_segment_periods, 2)}</span>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="flex flex-col gap-4 pb-2 mb-4">
                <p className="font-semibold">New Contract Terms</p>

                <div className="flex flex-row items-start gap-3">

                    <div className="flex-1 inline-flex flex-col items-stretch">
                        {canManageClients &&  (
                            <React.Fragment>
                                <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                                    New Talent Rate
                                </p>
                                <div className="pt-1.5 flex items-stretch flex-col max-w-[200px]">

                                    <FormInputComponent
                                        fieldName="rate"
                                        value={formModel.rate}
                                        type="antd-input"
                                        disabled={isDisabled}
                                        placeholder="--"
                                        min={0}
                                        label={false}
                                        required
                                    >
                                        <InputNumber
                                            controls={false}
                                            addonBefore={readOnly ? undefined : "$"}
                                            readOnly={readOnly}
                                            bordered={!readOnly}
                                            style={readOnly ? {padding: 0} : undefined}
                                            min="0"
                                            max="5000"
                                            step={1}
                                            precision={4}
                                            formatter={value => Number(value)}
                                        />
                                    </FormInputComponent>
                                </div>
                            </React.Fragment>
                        )}
                    </div>
                    <div className="flex-1 inline-flex flex-col items-stretch">
                        <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                            New Bill Rate
                        </p>
                        <div className="pt-1.5 flex items-stretch flex-col max-w-[200px]">

                            <FormInputComponent
                                fieldName="platform_rate"
                                value={formModel.platform_rate}
                                type="antd-input"
                                disabled={isDisabled}
                                placeholder="--"
                                min={0}
                                label={false}
                                required
                            >
                                <InputNumber
                                    controls={false}
                                    addonBefore={readOnly ? undefined : "$"}
                                    readOnly={readOnly}
                                    bordered={!readOnly}
                                    style={readOnly ? {padding: 0} : undefined}
                                    min="0"
                                    max="5000"
                                    step={1}
                                    precision={4}
                                    formatter={value => Number(value)}
                                />
                            </FormInputComponent>
                        </div>
                    </div>
                    <div className="flex-1">
                        {canManageClients &&  (
                            <React.Fragment>
                                <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                                    New Rate Percent Markup
                                </p>

                                <div className="pt-1.5 flex flex-row items-center gap-2">
                                    <div className="pt-1.5 flex flex-row items-center gap-2">
                                        <span className="flex flex-row items-center gap-3">
                                            <span>{parseFloat(Number(formModel.percent_markup * 100).toFixed(4))}%</span>
                                        </span>
                                    </div>
                                </div>
                            </React.Fragment>
                        )}
                    </div>

                </div>


                <div className="flex flex-row items-start gap-3">

                    <div className="flex-1">
                        <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                            End Date for New Rate
                        </p>
                        <div className="pt-1.5 flex items-stretch flex-col max-w-[200px]">

                            <FormInputComponent
                                fieldName="ended_at"
                                value={TB.moment(formModel.ended_at)}
                                label={false}
                                type="date"
                                readOnly={readOnly}
                                bordered={!readOnly}
                                disabled={isDisabled}
                                validationErrors={{
                                    isDefaultRequiredValue: TB.trans('validation.required', {
                                        'attribute': 'end date',
                                    })
                                }}
                                required
                            />

                        </div>

                    </div>


                    <div className="flex-1">
                        <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                            Est. Hours for New Rate
                        </p>
                        <div className="pt-1.5 flex items-stretch flex-col max-w-[200px]">

                            <FormInputComponent
                                fieldName="affected_periods"
                                value={formModel.affected_periods}
                                type="antd-input"
                                disabled={isDisabled}
                                placeholder="--"
                                min={0}
                                // max={formModel.original_total_periods}
                                label={false}
                                required
                            >
                                <InputNumber
                                    controls={false}
                                    readOnly={readOnly}
                                    bordered={!readOnly}
                                    style={readOnly ? {padding: 0} : undefined}
                                    defaultValue={lastCandidateSegment.affected_periods}
                                    step={1}
                                    formatter={value => Number(value)}
                                />
                            </FormInputComponent>
                        </div>
                    </div>
                    <div className="flex-1"/>

                </div>
                <div className="flex flex-row items-start gap-3">
                    <div className="flex-1">
                        {canManageClients &&  (
                            <React.Fragment>
                                <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                                    New Rate Resource Cost
                                </p>

                                <div className="pt-1.5 flex flex-row items-center gap-2">
                                    <div className="pt-1.5 flex flex-row items-center gap-2">
                                        <span className="flex flex-row items-center gap-3">

                                            <span>${TB.formatNumber(formModel.rate * formModel.affected_periods, 2)}</span>
                                        </span>
                                    </div>
                                </div>
                            </React.Fragment>
                        )}
                    </div>
                    <div className="flex-1">
                        {canManageClients &&  (
                            <React.Fragment>
                                <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                                    New Rate Platform Fee
                                </p>

                                <div className="pt-1.5 flex flex-row items-center gap-2">
                                    <div className="pt-1.5 flex flex-row items-center gap-2">
                                        <span className="flex flex-row items-center gap-3">

                                            <span>${TB.formatNumber((formModel.platform_rate * formModel.affected_periods) - (formModel.rate * formModel.affected_periods), 2)}</span>
                                        </span>
                                    </div>
                                </div>
                            </React.Fragment>
                        )}
                    </div>
                    <div className="flex-1">
                        <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                            New Rate Contract Value
                        </p>

                        <div className="pt-1.5 flex flex-row items-center gap-2">
                            <div className="pt-1.5 flex flex-row items-center gap-2">
                                <span className="flex flex-row items-center gap-3">
                                    <span>
                                        ${TB.formatNumber(formModel.platform_rate * formModel.affected_periods, 2)}</span>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    )
}
const CandidateInputs = ({formModel, setFormModel, readOnly, ...props}) => {
    const {auth} = useAuth();
    const canManageClients = auth.canManageClients();

    const {position, isDisabled} = usePosition();

    const lastCandidateSegment = useMemo(() => {
        let segment = TB.last(position.candidate_segments);
        segment.platform_fee = segment.platform_cost - segment.talent_cost;
        segment.candidate.user = User.make(segment.candidate.user)
        return segment;
    }, [position]);

    const lastCandidate = useMemo(() => {
        if (readOnly && formModel.original_candidate) {
            return  User.make(formModel.original_candidate?.user);
        }
        return lastCandidateSegment?.candidate?.user
    }, [readOnly, lastCandidateSegment])

    const newCandidate = useMemo(() => {

        return readOnly && formModel.new_candidate && User.make(formModel.new_candidate?.user);
    }, [formModel]);

    const getUserOption = (user) => {
        return {
            value: user.id,
            user: user,
            title: user.fullName(),
            label: (
                <span className="flex flex-row items-center gap-2 relative overflow-hidden flex-nowrap">
                    <UserImageCircle user={user} size={28}/>
                    <span className="whitespace-no-wrap">
                        {user.fullName()}
                    </span>
                    <span className="text-xxs text-info ml-auto whitespace-no-wrap text-ellipsis overflow-hidden">
                        {user.email}
                    </span>
                </span>
            )
        };
    }
    return (
        <React.Fragment>
            <div className="flex flex-col gap-4 pb-2 border-b border-wripplePrimaryRuleBlue mb-4">
                <p className="font-semibold">Current Contract Terms</p>

                <div className="flex flex-row items-start gap-3">
                    <div className="flex-1">
                        <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                            Talent Resource
                        </p>
                        <div className="pt-1.5 flex items-stretch flex-col">

                            {lastCandidate && (
                                <div className="pt-1.5 flex flex-row items-center gap-2">
                                    <UserImageCircle user={lastCandidate}/>
                                    <p>
                                        {lastCandidate.fullName()}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="flex-1">
                        {canManageClients &&  (
                            <React.Fragment>
                                <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                                    Tax Classification
                                </p>
                                <div className="pt-1.5 flex items-stretch flex-col">

                                    {lastCandidateSegment.candidate && (
                                        <p className="pt-1.5">
                                            {TaxTypes[lastCandidateSegment.candidate.tax_type]}
                                        </p>
                                    )}
                                </div>
                            </React.Fragment>
                        )}
                    </div>
                    <div className="flex-1">
                        <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                            Start Date
                        </p>
                        <div className="pt-1.5 flex items-stretch flex-col">

                            {lastCandidateSegment.started_at && (
                                <p className="pt-1.5">
                                    {TB.moment(lastCandidateSegment.started_at).format('MMM Do, YYYY')}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex flex-row items-start gap-3">
                    <div className="flex-1">
                        {canManageClients &&  (
                            <React.Fragment>
                                <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                                    Hourly Rate
                                </p>
                                <div className="pt-1.5 flex flex-row items-center gap-2">
                                    <div className="pt-1.5 flex flex-row items-center gap-2">
                                        ${TB.formatNumber((lastCandidateSegment.rate || lastCandidateSegment.hourly_rate), 2)} / hr
                                    </div>
                                </div>
                            </React.Fragment>
                        )}
                    </div>
                    <div className="flex-1">
                        <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                            Bill Rate
                        </p>
                        <div className="pt-1.5 flex flex-row items-center gap-2">
                            <div className="pt-1.5 flex flex-row items-center gap-2">
                                ${TB.formatNumber(lastCandidateSegment.platform_rate, 2)} / hr
                            </div>
                        </div>
                    </div>
                    <div className="flex-1">
                        {canManageClients &&  (
                            <React.Fragment>
                                <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                                    Percent Markup
                                </p>

                                <div className="pt-1.5 flex flex-row items-center gap-2">
                                    <div className="pt-1.5 flex flex-row items-center gap-2">
                                        <span className="flex flex-row items-center gap-3">
                                            <span>{parseFloat(Number(lastCandidateSegment.percent_markup * 100).toFixed(4))}%</span>
                                        </span>
                                    </div>
                                </div>
                            </React.Fragment>
                        )}
                    </div>
                </div>
                <div className="flex flex-row items-start gap-3">

                    <div className="flex-1">
                        <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                            Current End Date
                        </p>
                        <div className="pt-1.5 flex items-stretch flex-col">

                            {lastCandidateSegment.ended_at && (
                                <p className="pt-1.5">
                                    {TB.moment(lastCandidateSegment.ended_at).format('MMM Do, YYYY')}
                                </p>
                            )}
                        </div>
                    </div>
                    <div className="flex-1">
                        <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                            New End Date
                        </p>
                        <div className="pt-1.5 flex items-stretch flex-col max-w-[200px]">

                            <FormInputComponent
                                fieldName="effective_at"
                                value={TB.moment(formModel.effective_at)}
                                label={false}
                                type="date"
                                readOnly={readOnly}
                                bordered={!readOnly}
                                disabled={isDisabled}
                                validationErrors={{
                                    isDefaultRequiredValue: TB.trans('validation.required', {
                                        'attribute': 'end date',
                                    })
                                }}
                                required
                            />

                        </div>
                    </div>

                    <div className="flex-1"/>
                </div>
                <div className="flex flex-row items-start gap-3">

                    <div className="flex-1">
                        <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                            Current Total Hours
                        </p>

                        <div className="pt-1.5 flex items-stretch flex-col">

                            <p className="pt-1.5">
                                {lastCandidateSegment.total_periods || lastCandidateSegment.total_hours || '-'}
                            </p>

                        </div>
                    </div>
                    <div className="flex-1">
                        <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                            New Total Hours
                        </p>
                        <div className="pt-1.5 flex items-stretch flex-col max-w-[200px]">

                            <FormInputComponent
                                fieldName="last_segment_periods"
                                value={formModel.last_segment_periods}
                                type="antd-input"
                                disabled={isDisabled}
                                placeholder="--"
                                min={0}
                                // max={formModel.original_total_periods}
                                label={false}
                                required
                            >
                                <InputNumber
                                    controls={false}
                                    readOnly={readOnly}
                                    bordered={!readOnly}
                                    style={readOnly ? {padding: 0} : undefined}
                                    defaultValue={lastCandidateSegment.total_periods || lastCandidateSegment.total_hours}
                                    step={1}
                                    formatter={value => Number(value)}
                                />
                            </FormInputComponent>
                        </div>
                    </div>

                    <div className="flex-1"/>
                </div>

                <div className="flex flex-row items-start gap-3">
                    <div className="flex-1">
                        {canManageClients &&  (
                            <React.Fragment>
                                <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                                    Resource Cost
                                </p>

                                <div className="pt-1.5 flex flex-row items-center gap-2">
                                    <div className="pt-1.5 flex flex-row items-center gap-2">
                                        <span className="flex flex-row items-center gap-3">
                                            {Number(lastCandidateSegment.talent_cost).toFixed(2) !== Number((lastCandidateSegment.rate || lastCandidateSegment.hourly_rate) * formModel.last_segment_periods).toFixed(2) && (
                                                <span className="text-danger line-through">${TB.formatNumber(lastCandidateSegment.talent_cost, 2)}</span>
                                            )}
                                            <span>${TB.formatNumber((lastCandidateSegment.rate || lastCandidateSegment.hourly_rate) * formModel.last_segment_periods, 2)}</span>
                                        </span>
                                    </div>
                                </div>
                            </React.Fragment>
                        )}
                    </div>
                    <div className="flex-1">
                        {canManageClients &&  (
                            <React.Fragment>

                                <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                                    Platform Fee
                                </p>

                                <div className="pt-1.5 flex flex-row items-center gap-2">
                                    <div className="pt-1.5 flex flex-row items-center gap-2">
                                        <span className="flex flex-row items-center gap-3">
                                            {Number(lastCandidateSegment.platform_fee).toFixed(2) !== Number((lastCandidateSegment.platform_rate * formModel.last_segment_periods) - ((lastCandidateSegment.hourly_rate || lastCandidateSegment.rate) * formModel.last_segment_periods)).toFixed(2) && (
                                                <span className="text-danger line-through">${TB.formatNumber(lastCandidateSegment.platform_fee, 2)}</span>
                                            )}
                                            <span>${TB.formatNumber((lastCandidateSegment.platform_rate * formModel.last_segment_periods) - ((lastCandidateSegment.hourly_rate || lastCandidateSegment.rate) * formModel.last_segment_periods), 2)}</span>
                                        </span>
                                    </div>
                                </div>
                            </React.Fragment>
                        )}
                    </div>
                    <div className="flex-1">
                        <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                            Contract Value
                        </p>

                        <div className="pt-1.5 flex flex-row items-center gap-2">
                            <div className="pt-1.5 flex flex-row items-center gap-2">
                                <span className="flex flex-row items-center gap-3">
                                    {Number(lastCandidateSegment.platform_cost).toFixed(2) !== Number(lastCandidateSegment.platform_rate * formModel.last_segment_periods).toFixed(2) && (
                                        <span className="text-danger line-through">${TB.formatNumber(lastCandidateSegment.platform_cost, 2)}</span>
                                    )}
                                    <span>${TB.formatNumber(lastCandidateSegment.platform_rate * formModel.last_segment_periods, 2)}</span>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="flex flex-col gap-4 pb-2 mb-4">
                <p className="font-semibold">New Contract Terms</p>


                <div className="flex flex-row items-start gap-4.5">
                    <div style={{flex: 2}}>

                        <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                            New Talent Resource
                        </p>

                            {readOnly ? (newCandidate && (
                                <div className="pt-1.5 flex flex-row items-center gap-2">
                                    <UserImageCircle user={newCandidate}/>
                                    <p>
                                        {newCandidate.fullName()}
                                    </p>
                                </div>
                            )) : (
                                <div className="pt-1.5 flex items-stretch flex-col max-w-[460px]">
                                    <SearchTalentInput
                                        params={{
                                            disciplines: TB.map(position?.disciplines, 'id'),
                                            // hide_matching: 1,
                                            sort_by: 'min_bill_rate'
                                        }}
                                        perPage={20}
                                        readOnly={readOnly}
                                        bordered={!readOnly}
                                        placeholder="Search for talent"
                                        isDisabled={isDisabled}
                                        formatOption={(member) => {
                                            let disciplines = member.matching?.disciplines?.disciplines;

                                            let user = User.make({...member, disciplines})
                                            return getUserOption(user)
                                        }}
                                        inputValue={formModel.new_talent}
                                        onChange={(value, option) => {
                                            if (!formModel.new_talent?.user?.id && !option) {
                                                return
                                            }
                                            // console.log('onChangeResource', option)
                                            return setFormModel({
                                                ...formModel,
                                                new_talent: option,
                                            });
                                        }
                                        }/>
                                </div>
                            )}
                    </div>
                    <div className="flex-1">
                        {canManageClients &&  (
                            <React.Fragment>
                                <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                                    Tax Classification
                                </p>
                                <div className="pt-1.5 flex items-stretch flex-col">

                                    {lastCandidateSegment.candidate && (
                                        <p className="pt-1.5">
                                            {TaxTypes[lastCandidateSegment.candidate.tax_type]}
                                        </p>
                                    )}
                                </div>
                            </React.Fragment>
                        )}
                    </div>

                </div>
                <div className="flex flex-row items-start gap-3">
                    <div className="flex-1">
                        <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                            Start Date
                        </p>
                        <div className="pt-1.5 flex items-stretch flex-col">

                            {formModel.effective_at && (
                                <p className="pt-1.5">
                                    {TB.moment(formModel.effective_at).format('MMM Do, YYYY')}
                                </p>
                            )}
                        </div>
                    </div>
                    <div className="flex-1">
                        <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                            New End Date
                        </p>
                        <div className="pt-1.5 flex items-stretch flex-col max-w-[200px]">

                            <FormInputComponent
                                fieldName="ended_at"
                                value={TB.moment(formModel.ended_at)}
                                label={false}
                                type="date"
                                readOnly={readOnly}
                                bordered={!readOnly}
                                disabled={(!readOnly && !formModel.new_talent) || isDisabled}
                                minDate={formModel.effective_at ? TB.moment(formModel.effective_at)
                                    .endOf('week')
                                    .toDate() : undefined}
                                validationErrors={{
                                    isDefaultRequiredValue: TB.trans('validation.required', {
                                        'attribute': 'end date',
                                    })
                                }}
                                required
                            />

                        </div>
                    </div>
                    <div className="flex-1">
                        <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                            Total Hours
                        </p>
                        <div className="pt-1.5 flex items-stretch flex-col max-w-[200px]">

                            <FormInputComponent
                                fieldName="affected_periods"
                                value={formModel.affected_periods}
                                type="antd-input"
                                disabled={(!readOnly && !formModel.new_talent) || isDisabled}
                                placeholder="--"
                                min={0}
                                // max={formModel.original_total_periods}
                                label={false}
                                required
                            >
                                <InputNumber
                                    controls={false}
                                    readOnly={readOnly}
                                    bordered={!readOnly}
                                    style={readOnly ? {padding: 0} : undefined}
                                    defaultValue={formModel.affected_periods}
                                    step={1}
                                    formatter={value => Number(value)}
                                />
                            </FormInputComponent>
                        </div>
                    </div>
                </div>
                <div className="flex flex-row items-start gap-3">
                    <div className="flex-1 inline-flex flex-col items-stretch">
                        {canManageClients &&  (
                            <React.Fragment>
                                <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                                    New Talent Rate
                                </p>
                                <div className="pt-1.5 flex items-stretch flex-col max-w-[200px]">

                                    <FormInputComponent
                                        fieldName="rate"
                                        value={formModel.rate}
                                        type="antd-input"
                                        disabled={(!readOnly && !formModel.new_talent) || isDisabled}
                                        placeholder="--"
                                        min={0}
                                        label={false}
                                        required
                                    >
                                        <InputNumber
                                            controls={false}
                                            readOnly={readOnly}
                                            bordered={!readOnly}
                                            style={readOnly ? {padding: 0} : undefined}
                                            addonBefore="$"
                                            min="0"
                                            max="5000"
                                            step={1}
                                            precision={4}
                                            formatter={value => Number(value)}
                                        />
                                    </FormInputComponent>
                                </div>
                            </React.Fragment>
                        )}
                    </div>
                    <div className="flex-1 inline-flex flex-col items-stretch">
                        <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                            New Bill Rate
                        </p>
                        <div className="pt-1.5 flex items-stretch flex-col max-w-[200px]">

                            <FormInputComponent
                                fieldName="platform_rate"
                                value={formModel.platform_rate}
                                type="antd-input"
                                disabled={(!readOnly && !formModel.new_talent) || isDisabled}
                                placeholder="--"
                                min={0}
                                label={false}
                                required
                            >
                                <InputNumber
                                    controls={false}
                                    readOnly={readOnly}
                                    bordered={!readOnly}
                                    style={readOnly ? {padding: 0} : undefined}
                                    addonBefore="$"
                                    min="0"
                                    max="5000"
                                    step={1}
                                    precision={4}
                                    formatter={value => Number(value)}
                                />
                            </FormInputComponent>
                        </div>
                    </div>
                    <div className="flex-1">
                        {canManageClients &&  (
                            <React.Fragment>
                                <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                                    New Percent Markup
                                </p>

                                <div className="pt-1.5 flex flex-row items-center gap-2">
                                    <div className="pt-1.5 flex flex-row items-center gap-2">
                                        <span className="flex flex-row items-center gap-3">
                                            <span>{parseFloat(Number(formModel.percent_markup * 100).toFixed(4))}%</span>
                                        </span>
                                    </div>
                                </div>
                            </React.Fragment>
                        )}
                    </div>
                </div>
                <div className="flex flex-row items-start gap-3">
                    <div className="flex-1">
                        {canManageClients &&  (
                            <React.Fragment>
                                <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                                    New Resource Cost
                                </p>

                                <div className="pt-1.5 flex flex-row items-center gap-2">
                                    <div className="pt-1.5 flex flex-row items-center gap-2">
                                        <span className="flex flex-row items-center gap-3">

                                            <span>${TB.formatNumber(formModel.rate * formModel.affected_periods, 2)}</span>
                                        </span>
                                    </div>
                                </div>
                            </React.Fragment>
                        )}
                    </div>
                    <div className="flex-1">
                        {canManageClients &&  (
                            <React.Fragment>
                                <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                                    New Platform Fee
                                </p>

                                <div className="pt-1.5 flex flex-row items-center gap-2">
                                    <div className="pt-1.5 flex flex-row items-center gap-2">
                                        <span className="flex flex-row items-center gap-3">

                                            <span>${TB.formatNumber((formModel.platform_rate * formModel.affected_periods) - (formModel.rate * formModel.affected_periods), 2)}</span>
                                        </span>
                                    </div>
                                </div>
                            </React.Fragment>
                        )}
                    </div>
                    <div className="flex-1">
                        <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue">
                            New Contract Value
                        </p>

                        <div className="pt-1.5 flex flex-row items-center gap-2">
                            <div className="pt-1.5 flex flex-row items-center gap-2">
                                <span className="flex flex-row items-center gap-3">
                                    <span>
                                        ${TB.formatNumber(formModel.platform_rate * formModel.affected_periods, 2)}</span>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    )
}
const {TypeTimeline, TypeRate, TypeCandidate} = ProjectPosition.ChangeOrderTypes;
const typeLabels = {
    Timeline: 'Timeline Change',
    Rate: 'Rate/Cost Change',
    Candidate: 'Resource Swap'
}
const TaxTypes = {
    1: '1099',
    2: 'W2',
}
const ChangeOrderItemForm = ({order, onClose, onSubmit, isDisabled}) => {
    const formRef = useRef(null);
    const {auth} = useAuth();
    // const inputFile = useRef(null);
    // const { formModel, isDisabled, onFormChange } = usePositionForm();
    const [impactContainer, setInputContainer] = React.useState(null);

    // const impactContainer = useRef(null);
    const {position} = usePosition();

    const [formModel, setFormModel] = useState(() => {

        let co = TB.find(order.items, ['position_id', position.id]) || position.makeNewChangeOrder();
        if(co.latest_contract) {
            co.contract = co.latest_contract;
        }
        if(co.latest_signed_contract) {
            co.signed_contract = co.latest_signed_contract;
        }
        return co;
    })

    const lastCandidateSegment = useMemo(() => {
        let segment = TB.last(position.candidate_segments);
        segment.platform_fee = segment.platform_cost - segment.talent_cost;
        segment.candidate.user = User.make(segment.candidate.user)
        return segment;
    }, [position]);

    // console.log('ChangeOrderItemForm', parentOrder)
    const [searchParams, setSearchParams] = useSearchParams();

    const candidate = useMemo(() => {
        return position.assignedCandidate();
    }, [position?.id, isDisabled, position?.is_filled]);


    const handleSubmit = async () => {
        let {
            new_talent,
            ...coProps
        } = {...formModel};

        if (new_talent) {
            coProps.new_talent_id = new_talent.value;
        }
        coProps.position_id = position.id;

        if (coProps.type === TypeTimeline) {
            let previousTotalHours = TB.last(position.change_orders)?.new_total_periods || position.actual_total_periods
            let previousCandidateHours = previousTotalHours - lastCandidateSegment.total_periods;

            coProps.total_hours += previousCandidateHours;
        }


        return onSubmit(coProps);
    }

    const uploadProps = (name) => ({
        onRemove: async (file) => {
            setFormModel({...formModel, [`${name}_at`]: null, [`${name}_path`]: undefined});
        },
        beforeUpload: (file) => {
            setFormModel({...formModel, [`${name}_path`]: file});
            return false;
        },
        fileList: formModel[`${name}_path`] ? [formModel[`${name}_path`]] : [],
        showUploadList: {
            showDownloadIcon: true,
            showRemoveIcon: true,
            showPreviewIcon: true
        },
        multiple: false,
        maxCount: 1,
    });

    useMount(() => {
        console.log('ChangeOrderItem', formModel)
    })

    const readOnly = order.published_at || !auth.canManageClients() || formModel.id;

    const typeOptions = readOnly ? [
        {label: typeLabels[formModel.type], value: formModel.type, style: {lineHeight: '35px', height: 35}},
    ] : [
        {label: typeLabels.Timeline, value: TypeTimeline, style: {lineHeight: '35px', height: 35}},
        {label: typeLabels.Rate, value: TypeRate, style: {lineHeight: '35px', height: 35}},
        {label: typeLabels.Candidate, value: TypeCandidate, style: {lineHeight: '35px', height: 35}}
    ]

    return (
        <SpinLoader isLoading={isDisabled}>
            <div className="bg-white text-left">

                {/*<div className="candidate-info flex gap-4 pt-4">*/}
                {/*    {candidate ? (*/}
                {/*        <UserImageCircle*/}
                {/*            user={candidate}*/}
                {/*            hideTooltip={true}*/}
                {/*            size={60} className="img-fluid img-grayscale"/>*/}
                {/*    ) : (*/}
                {/*        <i className="img-fluid fas fa-user-circle text-6xl"/>*/}
                {/*    )}*/}

                {/*    <div className="flex flex-col justify-center flex-grow gap-3">*/}
                {/*        <div className="flex flex-row justify-start items-center gap-4">*/}
                {/*            <p className="text-xl text-primary-100">*/}
                {/*                {candidate ? candidate.firstNamePlusInitial() : 'TBD'}*/}
                {/*            </p>*/}

                {/*            {candidate?.hasMatching() && (*/}
                {/*                <div className="flex flex-col">*/}
                {/*                    <div className="flex flex-row items-center">*/}
                {/*                        <SimpleCircleProgress*/}
                {/*                            stroke={18}*/}
                {/*                            width="16px"*/}
                {/*                            className="mr-2"*/}
                {/*                            value={candidate.overallMatchPercent()}/>*/}

                {/*                        <AnimatedNumber*/}
                {/*                            className="font-semibold text-xs mb-0"*/}
                {/*                            value={candidate.overallMatchPercent()}*/}
                {/*                            formatValue={(value) => `${TB.round(value)}% Total Match`}*/}
                {/*                            duration={1000}/>*/}
                {/*                    </div>*/}
                {/*                </div>*/}
                {/*            )}*/}
                {/*        </div>*/}

                {/*        <p>*/}
                {/*            <b>Position:</b> {position.title}*/}
                {/*        </p>*/}
                {/*    </div>*/}
                {/*</div>*/}
                <Form
                    ref={formRef}
                    className="change-order-form-container"
                    onSubmit={handleSubmit}
                    submitButton={false}
                    background={"none"}
                    isDisabled={isDisabled}
                    model={formModel}
                    onChange={async (model) => {
                        let updatedFormModel = {...formModel};

                        for (let [key, value] of Object.entries(model)) {
                            updatedFormModel = TB.set(updatedFormModel, key, value);
                        }
                        // if (type === TypeTimeline && updatedFormModel.new_total_periods) {
                        //     updatedFormModel.affected_periods = updatedFormModel.new_total_periods - updatedFormModel.original_total_periods;
                        // }

                        // if (formModel.type !== updatedFormModel.type) {
                        //
                        //     return formRef.current?.forceReset()
                        // }

                        if (updatedFormModel.type === TypeRate || updatedFormModel.type === TypeCandidate) {
                            if ((model.platform_rate !== formModel.platform_rate) || (model.percent_markup !== formModel.percent_markup)) {
                                updatedFormModel.percent_markup = Number(
                                    (updatedFormModel.platform_rate - updatedFormModel.rate) / updatedFormModel.rate
                                ).toFixed(4);
                            }
                        }

                        switch (updatedFormModel.type) {
                            case TypeTimeline:
                                updatedFormModel.talent_cost = updatedFormModel.rate * updatedFormModel.total_periods;
                                updatedFormModel.platform_cost = updatedFormModel.platform_rate * updatedFormModel.total_periods;
                                updatedFormModel.platform_fee = updatedFormModel.platform_cost - updatedFormModel.talent_cost;
                                break;

                            case TypeRate:
                            case TypeCandidate:
                                updatedFormModel.talent_cost = Number(((lastCandidateSegment.hourly_rate || lastCandidateSegment.rate) * updatedFormModel.last_segment_periods) + (updatedFormModel.rate * updatedFormModel.affected_periods)).toFixed(2);
                                updatedFormModel.platform_cost = Number((lastCandidateSegment.platform_rate * updatedFormModel.last_segment_periods) + (updatedFormModel.platform_rate * updatedFormModel.affected_periods)).toFixed(2);
                                updatedFormModel.platform_fee = Number(updatedFormModel.platform_cost - updatedFormModel.talent_cost).toFixed(2);
                                break;
                        }
                        console.log('updatedformmmofdlafd', updatedFormModel)

                        await setFormModel(updatedFormModel)
                    }}
                    fields={[]}
                >

                    <div className="flex gap-4">

                        <div className="w-2/3 flex flex-col gap-3">
                            <div className="flex flex-col gap-4 pb-6 border-b border-wripplePrimaryRuleBlue mb-4">
                                <p className="font-semibold">
                                    Item Details
                                </p>
                                <div className="flex flex-col">
                                    <div className="flex flex-row gap-4">
                                        <div className="flex-grow">
                                            <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue mb-2.5">
                                                Order Type
                                            </p>

                                            <div className={readOnly ? 'pointer-events-none' : ''}>
                                                <Radio.Group
                                                    options={typeOptions}
                                                    onChange={async (e) => {

                                                        let updatedModel = position.makeNewChangeOrder(e.target.value);
                                                        formRef.current?.forceReset(updatedModel);

                                                        setFormModel(updatedModel)

                                                    }}
                                                    readOnly={readOnly}
                                                    disabled={isDisabled}
                                                    value={formModel.type}
                                                    buttonStyle="solid"
                                                    optionType="button"/>
                                            </div>

                                        </div>
                                    </div>
                                </div>
                            </div>
                            {formModel.type === TypeTimeline && <TimelineInputs formModel={formModel} readOnly={readOnly}/>}
                            {formModel.type === TypeRate && <RateInputs formModel={formModel} readOnly={readOnly}/>}
                            {formModel.type === TypeCandidate && <CandidateInputs setFormModel={setFormModel} formModel={formModel} readOnly={readOnly}/>}
                            {/*{(auth.canManageClients() || (formModel.contract || formModel.signed_contract)) && (*/}
                            {/*    <div className="flex flex-col gap-4 pb-6 border-b border-wripplePrimaryRuleBlue mb-4">*/}
                            {/*        <p className="font-semibold">*/}
                            {/*            Documents*/}
                            {/*        </p>*/}
                            {/*        <div className="flex flex-col">*/}
                            {/*            <div className="flex flex-row gap-4">*/}
                            {/*                {auth.canManageClients() && (*/}
                            {/*                    <div className="flex-1">*/}
                            {/*                        <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue mb-2.5">*/}
                            {/*                            Talent Contract*/}
                            {/*                        </p>*/}

                            {/*                        <FormInputComponent*/}
                            {/*                            fieldName="contract"*/}
                            {/*                            value={formModel.contract}*/}
                            {/*                            label={false}*/}
                            {/*                            type="file"*/}
                            {/*                            disabled={isDisabled}*/}
                            {/*                            readOnly={readOnly}*/}
                            {/*                            bordered={!readOnly}*/}
                            {/*                            size={formModel?.contract?.size}*/}
                            {/*                            viewable={!!formModel?.contract?.id}*/}
                            {/*                            fileNameDisplay={(fileData) => {*/}
                            {/*                                if (typeof formModel?.contract === 'string') {*/}
                            {/*                                    return formModel?.contract;*/}
                            {/*                                }*/}
                            {/*                                return formModel?.contract?.name || fileData.name;*/}
                            {/*                            }}*/}
                            {/*                        />*/}

                            {/*                    </div>*/}
                            {/*                )}*/}
                            {/*                {(auth.canManageClients() || formModel.signed_contract) && (*/}
                            {/*                    <div className="flex-1">*/}
                            {/*                        <p className="text-sm font-medium text-left text-wripplePrimaryLightBlue mb-2.5">*/}
                            {/*                            Signed Talent Contract*/}
                            {/*                        </p>*/}

                            {/*                        <FormInputComponent*/}
                            {/*                            fieldName="signed_contract"*/}
                            {/*                            value={formModel.signed_contract}*/}
                            {/*                            label={false}*/}
                            {/*                            type="file"*/}
                            {/*                            disabled={isDisabled}*/}
                            {/*                            readOnly={readOnly}*/}
                            {/*                            bordered={!readOnly}*/}
                            {/*                            size={formModel?.signed_contract?.size}*/}
                            {/*                            viewable={!!formModel?.signed_contract?.id}*/}
                            {/*                            fileNameDisplay={(fileData) => {*/}
                            {/*                                if (typeof formModel?.signed_contract === 'string') {*/}
                            {/*                                    return formModel?.signed_contract;*/}
                            {/*                                }*/}
                            {/*                                return formModel?.signed_contract?.name || fileData.name;*/}
                            {/*                            }}*/}
                            {/*                        />*/}

                            {/*                    </div>*/}
                            {/*                )}*/}
                            {/*            </div>*/}
                            {/*        </div>*/}
                            {/*    </div>*/}
                            {/*)}*/}
                        </div>

                        <Affix
                            offsetTop={20}
                            target={document.querySelector('.ant-drawer-body') ? () => document.querySelector('.ant-drawer-body') : undefined}
                               className="flex-grow mt-10">
                            <ChangeOrderItemImpactPanel item={formModel}/>
                        </Affix>
                    </div>

                    <div className="pointer-events-auto z-50">
                        {readOnly ? (
                            <div className="flex justify-end gap-4 pt-4 border-t border-wripplePrimaryRuleBlue">
                                <button type="button" onClick={onClose} className="btn btn-outline-primary">
                                    Close
                                </button>
                            </div>
                        ) : (
                            <div className="flex justify-end gap-4 pt-4 border-t border-wripplePrimaryRuleBlue">
                                <button type="submit" className="btn btn-primary">
                                    Save
                                </button>

                                <button type="button" onClick={onClose} className="btn btn-outline-primary">
                                    Cancel
                                </button>
                            </div>
                        )}
                    </div>
                </Form>

            </div>
        </SpinLoader>
    )
}

export default ChangeOrderItemForm;